import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  useEditMode,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

/**
 * Sister Properties – the six Price Brothers communities closest to
 * Highland Park, ordered nearest first. Each card shows the community's
 * official hero shot, its name, the full address (linking out to Google
 * Maps), and a Visit Website CTA. Empty slots are hidden on the live site
 * and shown as placeholders in edit mode.
 */

// ─── Slot group definitions (one literal per community — schema extractor) ─

const community1Def: SlotGroupDefinition = {
  label: 'Community 1',
  description: 'Closest sister community. Leave fields empty to hide this card on the live site.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue: 'https://media.apts247.info/50/50b13584682a4500a2d84a1392297d93/hero_shot/community/0O7A4404.jpg',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue:
        'Kidney-shaped pool at Waterford Place Apartments & Townhomes ringed by tan lounge chairs, with mature shade trees and two-story stone-and-siding townhomes behind it.',
      generator: {
        prompt:
          'Write specific alt text for the photo on this card: name the community and describe what is actually visible (e.g. brick mid-rise exterior, pool deck at dusk, lobby seating). Avoid generic phrases like "apartment community", "property photo", or "exterior view".',
        length: '1 sentence',
      },
    },
    name: { label: 'Community Name', type: 'text', defaultValue: 'Waterford Place Apartments & Townhomes' },
    location: { label: 'Full Address', type: 'text', defaultValue: '11220 W. 108th Street, Overland Park, KS 66210' },
    url: {
      label: 'Map Link (Address)',
      type: 'url',
      defaultValue: 'https://www.google.com/maps/search/?api=1&query=Waterford+Place+Apartments+%26+Townhomes%2C+11220+W.+108th+Street%2C+Overland+Park%2C+KS+66210',
    },
    website: { label: 'Website', type: 'url', defaultValue: 'https://www.waterfordplaceaptskc.com/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const community2Def: SlotGroupDefinition = {
  label: 'Community 2',
  description: '2nd-closest sister community. Leave fields empty to hide this card on the live site.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue: 'https://media.apts247.info/b9/b9e0b9437e3c4fd7bbf4e0e5774a68eb/hero_shot/community/The-Claremont.jpg',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue:
        'Pool at The Claremont Apartment Homes with a white pergola, poolside fountains, and four-story brick buildings with white balconies behind it.',
      generator: {
        prompt:
          'Write specific alt text for the photo on this card: name the community and describe what is actually visible (e.g. brick mid-rise exterior, pool deck at dusk, lobby seating). Avoid generic phrases like "apartment community", "property photo", or "exterior view".',
        length: '1 sentence',
      },
    },
    name: { label: 'Community Name', type: 'text', defaultValue: 'The Claremont Apartment Homes' },
    location: { label: 'Full Address', type: 'text', defaultValue: '11909 W. 109th Street, Overland Park, KS 66210' },
    url: {
      label: 'Map Link (Address)',
      type: 'url',
      defaultValue: 'https://www.google.com/maps/search/?api=1&query=The+Claremont+Apartment+Homes%2C+11909+W.+109th+Street%2C+Overland+Park%2C+KS+66210',
    },
    website: { label: 'Website', type: 'url', defaultValue: 'https://www.claremontaptskc.com/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const community3Def: SlotGroupDefinition = {
  label: 'Community 3',
  description: '3rd-closest sister community. Leave fields empty to hide this card on the live site.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue: 'https://media.apts247.info/83/83a95c1f50cd46f8b36a329391146c6a/hero_shot/community/rIMG_3052.jpg',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue:
        'Pool at Crowne Chase Apartment Homes with wood pergolas over lounge chairs, umbrella-shaded patio tables, and a stainless steel grill under tall trees.',
      generator: {
        prompt:
          'Write specific alt text for the photo on this card: name the community and describe what is actually visible (e.g. brick mid-rise exterior, pool deck at dusk, lobby seating). Avoid generic phrases like "apartment community", "property photo", or "exterior view".',
        length: '1 sentence',
      },
    },
    name: { label: 'Community Name', type: 'text', defaultValue: 'Crowne Chase Apartment Homes' },
    location: { label: 'Full Address', type: 'text', defaultValue: '11621 W. 118th Terrace, Overland Park, KS 66210' },
    url: {
      label: 'Map Link (Address)',
      type: 'url',
      defaultValue: 'https://www.google.com/maps/search/?api=1&query=Crowne+Chase+Apartment+Homes%2C+11621+W.+118th+Terrace%2C+Overland+Park%2C+KS+66210',
    },
    website: { label: 'Website', type: 'url', defaultValue: 'https://www.crownechase.com/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const community4Def: SlotGroupDefinition = {
  label: 'Community 4',
  description: '4th-closest sister community. Leave fields empty to hide this card on the live site.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue: 'https://media.apts247.info/29/2962675227cc45db9c8767a51304b051/hero_shot/community/Louisburg-Square.jpg',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue:
        'Covered, fenced patio at Louisburg Square Apartments & Townhomes with a round dining table, a cushioned outdoor sofa, and brick townhomes with mansard roofs beyond.',
      generator: {
        prompt:
          'Write specific alt text for the photo on this card: name the community and describe what is actually visible (e.g. brick mid-rise exterior, pool deck at dusk, lobby seating). Avoid generic phrases like "apartment community", "property photo", or "exterior view".',
        length: '1 sentence',
      },
    },
    name: { label: 'Community Name', type: 'text', defaultValue: 'Louisburg Square Apartments & Townhomes' },
    location: { label: 'Full Address', type: 'text', defaultValue: '9301 Santa Fe Lane, Overland Park, KS 66212' },
    url: {
      label: 'Map Link (Address)',
      type: 'url',
      defaultValue: 'https://www.google.com/maps/search/?api=1&query=Louisburg+Square+Apartments+%26+Townhomes%2C+9301+Santa+Fe+Lane%2C+Overland+Park%2C+KS+66212',
    },
    website: { label: 'Website', type: 'url', defaultValue: 'https://www.louisburgsquareapts.com/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const community5Def: SlotGroupDefinition = {
  label: 'Community 5',
  description: '5th-closest sister community. Leave fields empty to hide this card on the live site.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue: 'https://media.apts247.info/69/69dd4de6c3bc4d64bca6da641b62747c/hero_shot/community/0O7A4566.jpg',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue:
        'Curved pool at Pebblebrook Apartment Homes lined with lounge chairs and a gas grill, surrounded by tall leafy trees and black iron fencing.',
      generator: {
        prompt:
          'Write specific alt text for the photo on this card: name the community and describe what is actually visible (e.g. brick mid-rise exterior, pool deck at dusk, lobby seating). Avoid generic phrases like "apartment community", "property photo", or "exterior view".',
        length: '1 sentence',
      },
    },
    name: { label: 'Community Name', type: 'text', defaultValue: 'Pebblebrook Apartment Homes' },
    location: { label: 'Full Address', type: 'text', defaultValue: '7700 W. 95th Street, Overland Park, KS 66212' },
    url: {
      label: 'Map Link (Address)',
      type: 'url',
      defaultValue: 'https://www.google.com/maps/search/?api=1&query=Pebblebrook+Apartment+Homes%2C+7700+W.+95th+Street%2C+Overland+Park%2C+KS+66212',
    },
    website: { label: 'Website', type: 'url', defaultValue: 'https://www.pebblebrookapts.com/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const community6Def: SlotGroupDefinition = {
  label: 'Community 6',
  description: '6th-closest sister community. Leave fields empty to hide this card on the live site.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue: 'https://media.apts247.info/7c/7cadce4a37f942e080553979235045eb/hero_shot/community/rIMG_0111.jpg',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue:
        'Pool at Creekside Apartment Homes with a row of blue sling chairs along landscaped beds and a brick-and-siding apartment building behind it.',
      generator: {
        prompt:
          'Write specific alt text for the photo on this card: name the community and describe what is actually visible (e.g. brick mid-rise exterior, pool deck at dusk, lobby seating). Avoid generic phrases like "apartment community", "property photo", or "exterior view".',
        length: '1 sentence',
      },
    },
    name: { label: 'Community Name', type: 'text', defaultValue: 'Creekside Apartment Homes' },
    location: { label: 'Full Address', type: 'text', defaultValue: '11920 Oakmont St, Overland Park, KS 66213' },
    url: {
      label: 'Map Link (Address)',
      type: 'url',
      defaultValue: 'https://www.google.com/maps/search/?api=1&query=Creekside+Apartment+Homes%2C+11920+Oakmont+St%2C+Overland+Park%2C+KS+66213',
    },
    website: { label: 'Website', type: 'url', defaultValue: 'https://www.creeksideaptskc.com/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

function hasText(value: string | null | undefined): boolean {
  return Boolean(value && value.trim());
}

function isFilled(community: Record<string, string | null>): boolean {
  return hasText(community.name) || hasText(community.url) || hasText(community.image);
}

function CommunityCardBody({
  community,
  number,
  isEditing,
  filled,
}: {
  community: Record<string, string | null>;
  number: number;
  isEditing: boolean;
  filled: boolean;
}) {
  const mapHref = (community.url || '').trim();
  const website = (community.website || '').trim();
  const name = (community.name || '').trim();
  const location = (community.location || '').trim();
  const target = community._target || '_blank';
  const rel = target === '_blank' ? 'noopener noreferrer' : undefined;
  const newTab = target === '_blank' ? ' (opens in a new tab)' : '';
  const hasImage = hasText(community.image);

  return (
    <article
      className={`sister-card${filled ? '' : ' sister-card--empty'}`}
      aria-label={filled ? undefined : `Community ${number} — click to add details`}
    >
      {hasImage || isEditing ? (
        <div className="sister-card-media">
          {hasImage ? (
            <VibesitesImage
              url={community.image || ''}
              alt={community.alt || name || `Community ${number}`}
              className="sister-card-image"
              sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
            />
          ) : (
            <div className="sister-card-image-placeholder" aria-hidden="true" />
          )}
        </div>
      ) : null}

      <div className="sister-card-body">
        <h3 className="sister-card-name">
          {name && website ? (
            <a href={website} target={target} rel={rel} aria-label={`${name} website${newTab}`}>
              {name}
            </a>
          ) : (
            name || (isEditing ? `Community ${number}` : '')
          )}
        </h3>

        {location && mapHref ? (
          <a
            className="sister-card-address"
            href={mapHref}
            target={target}
            rel={rel}
            aria-label={`${location} — view on Google Maps${newTab}`}
          >
            {location}
          </a>
        ) : location ? (
          <p className="sister-card-address sister-card-address--plain">{location}</p>
        ) : isEditing && !filled ? (
          <p className="sister-card-address sister-card-hint">Add full address</p>
        ) : null}

        {website ? (
          <a
            className="sister-card-link"
            href={website}
            target={target}
            rel={rel}
            aria-label={name ? `Visit the ${name} website${newTab}` : `Visit website${newTab}`}
          >
            Visit Website
          </a>
        ) : isEditing && filled ? (
          <span className="sister-card-link sister-card-link--muted">Add website URL</span>
        ) : null}
      </div>
    </article>
  );
}

export function SisterProperties() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();
  const editMode = useEditMode();
  const isEditing = Boolean(editMode?.isEditMode);

  const community1 = useSlotGroup('sisterproperties.community1', community1Def);
  const community2 = useSlotGroup('sisterproperties.community2', community2Def);
  const community3 = useSlotGroup('sisterproperties.community3', community3Def);
  const community4 = useSlotGroup('sisterproperties.community4', community4Def);
  const community5 = useSlotGroup('sisterproperties.community5', community5Def);
  const community6 = useSlotGroup('sisterproperties.community6', community6Def);

  return (
    <section ref={sectionRef} className="reveal-fade-up sister-properties">
      <div className="sister-properties-container">
        <div className="sister-properties-grid">
          {/*
            EditableSlotGroup groupPath must be a string literal for the schema
            linter — keep each wrapper inline rather than mapping a shared path.
          */}
          {(isEditing || isFilled(community1)) && (
            <EditableSlotGroup groupPath="sisterproperties.community1" definition={community1Def}>
              <CommunityCardBody community={community1} number={1} isEditing={isEditing} filled={isFilled(community1)} />
            </EditableSlotGroup>
          )}
          {(isEditing || isFilled(community2)) && (
            <EditableSlotGroup groupPath="sisterproperties.community2" definition={community2Def}>
              <CommunityCardBody community={community2} number={2} isEditing={isEditing} filled={isFilled(community2)} />
            </EditableSlotGroup>
          )}
          {(isEditing || isFilled(community3)) && (
            <EditableSlotGroup groupPath="sisterproperties.community3" definition={community3Def}>
              <CommunityCardBody community={community3} number={3} isEditing={isEditing} filled={isFilled(community3)} />
            </EditableSlotGroup>
          )}
          {(isEditing || isFilled(community4)) && (
            <EditableSlotGroup groupPath="sisterproperties.community4" definition={community4Def}>
              <CommunityCardBody community={community4} number={4} isEditing={isEditing} filled={isFilled(community4)} />
            </EditableSlotGroup>
          )}
          {(isEditing || isFilled(community5)) && (
            <EditableSlotGroup groupPath="sisterproperties.community5" definition={community5Def}>
              <CommunityCardBody community={community5} number={5} isEditing={isEditing} filled={isFilled(community5)} />
            </EditableSlotGroup>
          )}
          {(isEditing || isFilled(community6)) && (
            <EditableSlotGroup groupPath="sisterproperties.community6" definition={community6Def}>
              <CommunityCardBody community={community6} number={6} isEditing={isEditing} filled={isFilled(community6)} />
            </EditableSlotGroup>
          )}
        </div>
      </div>

      <style>{`
        .sister-properties {
          padding: 3.5rem 0 4rem;
          background: #ffffff;
        }
        @media (min-width: 768px) {
          .sister-properties { padding: 5rem 0 5.5rem; }
        }

        .sister-properties-container {
          max-width: 72rem;
          margin: 0 auto;
          padding: 0 1rem;
        }
        @media (min-width: 640px) { .sister-properties-container { padding: 0 1.5rem; } }
        @media (min-width: 1024px) { .sister-properties-container { padding: 0 2rem; } }

        .sister-properties-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.5rem;
        }
        @media (min-width: 640px) {
          .sister-properties-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (min-width: 1024px) {
          .sister-properties-grid { grid-template-columns: repeat(3, 1fr); gap: 1.75rem; }
        }

        .sister-card {
          display: flex;
          flex-direction: column;
          height: 100%;
          background: #ffffff;
          border: 1px solid #e8e8ea;
          border-radius: var(--vibe-radius-lg, 12px);
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
          transition: box-shadow 0.25s ease, border-color 0.25s ease;
        }
        .sister-card:hover {
          box-shadow: 0 8px 28px rgba(0, 0, 0, 0.08);
          border-color: #dddde1;
        }

        .sister-card--empty {
          border-style: dashed;
          box-shadow: none;
          background: #fafafa;
        }
        .sister-card--empty:hover {
          box-shadow: none;
          border-color: var(--vibe-color-primary);
        }

        .sister-card-media {
          position: relative;
          aspect-ratio: 4 / 3;
          background: #e8e8ea;
          overflow: hidden;
        }

        .sister-card-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          transition: transform 0.5s ease;
        }
        .sister-card:hover .sister-card-image {
          transform: scale(1.03);
        }
        @media (prefers-reduced-motion: reduce) {
          .sister-card-image { transition: none; }
          .sister-card:hover .sister-card-image { transform: none; }
        }

        .sister-card-image-placeholder {
          width: 100%;
          height: 100%;
          background:
            linear-gradient(135deg, #ececef 0%, #e2e3e6 50%, #ececef 100%);
        }

        .sister-card-body {
          display: flex;
          flex-direction: column;
          gap: 0.4rem;
          padding: 1.25rem 1.35rem 1.5rem;
          flex: 1;
        }

        .sister-card-name {
          font-family: var(--vibe-font-heading);
          font-size: 1.125rem;
          font-weight: 700;
          line-height: 1.3;
          text-transform: uppercase;
          letter-spacing: 0.03em;
          color: var(--vibe-color-text);
          margin: 0 0 0.15rem;
        }
        .sister-card-name a {
          color: inherit;
          text-decoration: none;
        }
        .sister-card-name a:hover {
          color: var(--vibe-color-primary);
        }

        .sister-card-address {
          font-family: var(--vibe-font-body);
          font-size: 0.9rem;
          line-height: 1.5;
          color: var(--vibe-color-text-muted);
          margin: 0;
          text-decoration: none;
        }
        a.sister-card-address {
          color: var(--vibe-color-primary);
          text-decoration: underline;
          text-underline-offset: 2px;
        }
        a.sister-card-address:hover {
          text-decoration-thickness: 2px;
        }
        .sister-card-hint {
          font-style: italic;
        }

        .sister-card-link {
          display: inline-flex;
          align-items: center;
          margin-top: auto;
          padding-top: 0.85rem;
          font-family: var(--vibe-font-heading);
          font-size: 0.8rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          color: var(--vibe-color-primary);
          text-decoration: none;
        }
        .sister-card-link:hover {
          text-decoration: underline;
        }
        .sister-card-link--muted {
          color: var(--vibe-color-text-muted);
          font-style: italic;
          text-transform: none;
          letter-spacing: 0;
          font-family: var(--vibe-font-body);
          font-weight: 400;
          font-size: 0.85rem;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'SisterProperties',
  displayName: 'Sister Properties',
  tokens: {},
  slotGroups: {
    'sisterproperties.community1': community1Def,
    'sisterproperties.community2': community2Def,
    'sisterproperties.community3': community3Def,
    'sisterproperties.community4': community4Def,
    'sisterproperties.community5': community5Def,
    'sisterproperties.community6': community6Def,
  },
};

(SisterProperties as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default SisterProperties;
